import { slugAtual } from './tenant.js';

/**
 * Cliente de API — Story 8.1.
 *
 * Duas responsabilidades, e só elas:
 *
 * 1. **Desembrulhar o envelope** `{success,data,error}`. Quem chama recebe
 *    `data` tipado e nunca precisa saber que existe envelope.
 * 2. **Traduzir falha em `ApiError` com o `code`**. A UI decide pelo **código**
 *    (`AUTH_FORBIDDEN`, `VALIDATION_ERROR`, …), jamais pelo texto da mensagem —
 *    texto muda, código é contrato.
 *
 * O refresh automático mora aqui porque é a única camada que enxerga todas as
 * requisições. Ver `renovarUmaVezSo`.
 */

export interface RespostaMeta {
  total: number;
  page: number;
  limit: number;
}

interface EnvelopeOk<T> {
  success: true;
  data: T;
  meta?: RespostaMeta;
}

interface EnvelopeErro {
  success: false;
  error: { code: string; message: string; retryAfter?: number };
}

export class ApiError extends Error {
  constructor(
    readonly code: string,
    mensagem: string,
    readonly status: number,
    readonly retryAfter?: number,
  ) {
    super(mensagem);
    this.name = 'ApiError';
  }

  /** Falha de rede/servidor sem envelope — não é erro de negócio. */
  static semResposta(detalhe: string): ApiError {
    return new ApiError('NETWORK_ERROR', `Não foi possível falar com o servidor. ${detalhe}`, 0);
  }
}

/**
 * Base da API.
 *
 * O default `/api` faz o dev usar o proxy do Vite e o container usar o
 * `proxy_pass` do nginx — nos dois casos, mesma origem, sem CORS e sem
 * `VITE_API_URL` para configurar. Um valor absoluto continua sendo aceito para
 * quando a API estiver em outro host (Traefik).
 */
const BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

/**
 * Token de acesso — **em memória**, nunca em storage.
 *
 * O **refresh não passa mais por aqui**: desde a Story 8.3 ele vive num cookie
 * `httpOnly` que o JavaScript não enxerga (ver `auth.routes.ts`). O navegador o
 * envia sozinho nas rotas `/api/auth/*`, e é por isso que `renovar()` e
 * `sair()` não recebem parâmetro nenhum.
 */
let accessToken: string | undefined;

export function definirAcesso(acesso: string): void {
  accessToken = acesso;
}

export function limparAcesso(): void {
  accessToken = undefined;
}

export function temSessao(): boolean {
  return accessToken !== undefined;
}

/**
 * Só para quem precisa montar uma requisição fora do cliente — hoje apenas o
 * download do PDF, cuja resposta é binária e não passa pelo envelope.
 */
export function obterAccessToken(): string | undefined {
  return accessToken;
}

/** Código plausível quando a resposta não traz um. Mantém a UI decidindo por código. */
function codigoPorStatus(status: number): string {
  if (status === 401) return 'AUTH_UNAUTHORIZED';
  if (status === 403) return 'AUTH_FORBIDDEN';
  if (status === 404) return 'RESOURCE_NOT_FOUND';
  if (status === 409) return 'VALIDATION_ERROR';
  if (status === 429) return 'RATE_LIMIT_EXCEEDED';
  if (status >= 500) return 'INTERNAL_ERROR';
  return 'VALIDATION_ERROR';
}

function cabecalhos(corpo: unknown, autenticar: boolean): Headers {
  const h = new Headers();
  // O tenant vai por header só quando o subdomínio não resolve (dev).
  const slug = slugAtual();
  if (slug !== undefined) h.set('x-tenant-slug', slug);
  // Sem corpo, NÃO mandamos content-type: o Fastify responde 400 a um POST que
  // se declara JSON e chega vazio.
  if (corpo !== undefined) h.set('content-type', 'application/json');
  if (autenticar && accessToken !== undefined) h.set('authorization', `Bearer ${accessToken}`);
  return h;
}

interface OpcoesRequisicao {
  metodo?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  corpo?: unknown;
  /** `false` nas rotas de sessão (login/refresh), que não levam access token. */
  autenticar?: boolean;
  /** Uso interno: impede que o retry após refresh entre em laço. */
  jaRenovou?: boolean;
  sinal?: AbortSignal;
}

export interface Pagina<T> {
  itens: T[];
  meta?: RespostaMeta;
}

/**
 * Renovação **deduplicada**.
 *
 * Uma tela costuma disparar várias requisições juntas. Se o token expirou, elas
 * falham todas ao mesmo tempo — e, sem esta trava, cada uma chamaria
 * `/auth/refresh`. Como o refresh é **rotacionado a cada uso**, o primeiro
 * invalidaria o dos outros e o usuário cairia para o login sem motivo. Por isso
 * existe **uma** promessa compartilhada por vez.
 */
let renovacaoEmCurso: Promise<boolean> | undefined;

/**
 * Troca o cookie de refresh por um access token novo.
 *
 * Sem corpo: quem carrega a credencial é o cookie. Exportado porque o
 * `AuthProvider` usa a mesma chamada para restaurar a sessão no primeiro
 * render — como o cookie é `httpOnly`, não há como perguntar "existe sessão?"
 * antes de tentar.
 */
export async function renovarSessao(): Promise<{ accessToken: string; usuario: unknown }> {
  const r = await requisicaoCrua<{ accessToken: string; usuario: unknown }>('/api/auth/refresh', {
    metodo: 'POST',
    autenticar: false,
    jaRenovou: true,
  });
  definirAcesso(r.dados.accessToken);
  return r.dados;
}

async function renovarUmaVezSo(): Promise<boolean> {
  renovacaoEmCurso ??= (async () => {
    try {
      await renovarSessao();
      return true;
    } catch {
      limparAcesso();
      return false;
    } finally {
      // Libera na volta do microtask para que os concorrentes leiam o resultado.
      queueMicrotask(() => {
        renovacaoEmCurso = undefined;
      });
    }
  })();
  return renovacaoEmCurso;
}

async function requisicaoCrua<T>(
  caminho: string,
  opcoes: OpcoesRequisicao,
): Promise<{ dados: T; meta?: RespostaMeta }> {
  const { metodo = 'GET', corpo, autenticar = true, sinal } = opcoes;

  let resposta: Response;
  try {
    resposta = await fetch(`${BASE}${caminho}`, {
      method: metodo,
      headers: cabecalhos(corpo, autenticar),
      // O cookie `httpOnly` do refresh só acompanha a requisição se as
      // credenciais forem enviadas. `same-origin` (e não `include`) porque o
      // front e a API compartilham origem via proxy — mandar credencial para
      // origem estranha seria abrir o que o `SameSite=Strict` fecha.
      credentials: 'same-origin',
      ...(corpo !== undefined ? { body: JSON.stringify(corpo) } : {}),
      ...(sinal !== undefined ? { signal: sinal } : {}),
    });
  } catch (erro) {
    if (erro instanceof DOMException && erro.name === 'AbortError') throw erro;
    throw ApiError.semResposta(erro instanceof Error ? erro.message : '');
  }

  let envelope: EnvelopeOk<T> | EnvelopeErro;
  try {
    envelope = (await resposta.json()) as EnvelopeOk<T> | EnvelopeErro;
  } catch {
    // 502 do proxy, HTML de erro do nginx, corpo vazio…
    throw new ApiError(
      codigoPorStatus(resposta.status),
      'Resposta inválida do servidor.',
      resposta.status,
    );
  }

  if (!envelope.success) {
    /*
     * Defesa contra resposta fora do envelope.
     *
     * Nem tudo que chega aqui passou pelos nossos handlers: um proxy, um
     * plugin ou um erro de infraestrutura pode responder noutro formato. Sem
     * este cuidado, `error.code` vinha `undefined`, `message` vinha `undefined`
     * e a tela mostrava uma **caixa de erro vazia** — pior que mensagem
     * genérica, porque o usuário não tem sequer o que relatar. Toda falha sai
     * daqui com código e texto.
     */
    const bruto = envelope.error as Partial<EnvelopeErro['error']> | undefined;
    const codigo =
      typeof bruto?.code === 'string' && bruto.code !== ''
        ? bruto.code
        : codigoPorStatus(resposta.status);
    const texto =
      typeof bruto?.message === 'string' && bruto.message.trim() !== ''
        ? bruto.message
        : `Falha na requisição (HTTP ${String(resposta.status)}).`;

    throw new ApiError(codigo, texto, resposta.status, bruto?.retryAfter);
  }

  return { dados: envelope.data, ...(envelope.meta !== undefined ? { meta: envelope.meta } : {}) };
}

/** Requisição com renovação transparente de sessão. */
export async function requisitar<T>(
  caminho: string,
  opcoes: OpcoesRequisicao = {},
): Promise<{ dados: T; meta?: RespostaMeta }> {
  try {
    return await requisicaoCrua<T>(caminho, opcoes);
  } catch (erro) {
    const expirou =
      erro instanceof ApiError &&
      (erro.code === 'AUTH_TOKEN_EXPIRED' || erro.code === 'AUTH_UNAUTHORIZED');

    if (!expirou || opcoes.jaRenovou === true || opcoes.autenticar === false) throw erro;

    const renovou = await renovarUmaVezSo();
    if (!renovou) throw erro;
    return requisicaoCrua<T>(caminho, { ...opcoes, jaRenovou: true });
  }
}

/** Açúcar para quem só quer os dados. */
export async function obter<T>(caminho: string, sinal?: AbortSignal): Promise<T> {
  const r = await requisitar<T>(caminho, { ...(sinal !== undefined ? { sinal } : {}) });
  return r.dados;
}

export async function enviar<T>(caminho: string, corpo?: unknown): Promise<T> {
  const r = await requisitar<T>(caminho, { metodo: 'POST', corpo });
  return r.dados;
}

/** Mensagem em pt-BR por **código**, com a do servidor como reserva. */
export function mensagemDoErro(erro: unknown): string {
  if (!(erro instanceof ApiError)) return 'Algo deu errado. Tente novamente.';

  switch (erro.code) {
    case 'AUTH_INVALID_CREDENTIALS':
      return 'E-mail ou senha inválidos.';
    case 'AUTH_TOKEN_EXPIRED':
      return 'Sua sessão expirou. Entre novamente.';
    case 'AUTH_FORBIDDEN':
      return 'Você não tem permissão para esta ação.';
    case 'TENANT_NOT_FOUND':
      return 'Empresa não encontrada. Confira o endereço de acesso.';
    case 'TENANT_SUSPENDED':
      return 'O acesso desta empresa está suspenso.';
    case 'RATE_LIMIT_EXCEEDED':
      return erro.retryAfter === undefined
        ? 'Muitas tentativas. Aguarde alguns minutos.'
        : `Muitas tentativas. Tente de novo em ${String(Math.ceil(erro.retryAfter / 60))} min.`;
    case 'NETWORK_ERROR':
      return 'Sem conexão com o servidor. Verifique a rede e tente de novo.';
    case 'INTERNAL_ERROR':
      return 'Erro no servidor. Tente novamente em instantes.';
    default:
      return erro.message;
  }
}
